/**
 * 应用主入口
 * 实现路由管理和状态逻辑，协调应用各组件之间的交互
 *
 * 核心功能：
 * 1. 配置全局Context提供者
 * 2. 设置路由配置和导航逻辑
 * 3. 处理应用状态切换和页面间的数据传递
 * 4. 协调文档准备和翻译功能之间的交互
 * 5. 管理错误处理和状态重置
 */

import React, { useEffect } from 'react';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async'; // 导入 Helmet
import { useAuthRouting } from '@readsavor/auth';
import { Header, Footer, GlobalAuthErrorHandler } from './components/Layout/index.js';
import { AppServiceProviders } from './providers/index.js';
import { NavigationProvider, AppRoutes } from './routes/index.js';

/**
 * 应用程序主组件
 *
 * 作为入口点，组织所有页面和上下文提供者
 */
const App: React.FC = () => {
  // const instanceIdRef = useRef(`app_${Math.random().toString(36).substr(2, 9)}`);

  // 添加生命周期日志
  useEffect(() => {
    return () => {};
  }, []);

  return (
    <Router>
      <AppServiceProviders>
        <GlobalAuthErrorHandler>
          <AppContent />
        </GlobalAuthErrorHandler>
      </AppServiceProviders>
    </Router>
  );
};

/**
 * 应用内容组件
 *
 * 分离出来以便可以使用Context hooks
 */
const AppContent: React.FC = () => {
  // const instanceIdRef = useRef(`appContent_${Math.random().toString(36).substr(2, 9)}`);
  const location = useLocation();

  // 🆕 Handle authentication-related routing
  useAuthRouting();

  // 添加生命周期日志
  useEffect(() => {
    return () => {};
  }, []);

  // 记录路由变化
  useEffect(() => {}, [location]);

  // 确保页面切换时滚动到顶部
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // 动态构建不带参数的 URL
  const canonicalUrl = `https://readsavor.com${location.pathname === '/' ? '' : location.pathname}`;

  return (
    <NavigationProvider>
      <Helmet>
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        <div className="flex-grow">
          <AppRoutes />
        </div>
        <Footer />
      </div>
    </NavigationProvider>
  );
};

export default App;
